import { Link } from "react-router-dom";

export default function EngineRulesPage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">⚡ 引擎規則</h1>
          <p className="text-sm text-slate-400 mt-1">
            這裡記錄 AIBot 的 LLM 路由、方案層級引擎矩陣、Vision 規則與節流策略——是 Dashboard 其他頁面看到的「後台真相」。
          </p>
        </div>
        <Link
          to="/anti-abuse"
          className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg border border-slate-700 transition-colors"
        >
          🛡️ 反濫用設定 →
        </Link>
      </div>

      {/* ── 方案對照表 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-emerald-400 mb-4">💎 方案層級（plan_tier）</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-800">
                <th className="pb-2 font-medium">方案</th>
                <th className="pb-2 font-medium">月費</th>
                <th className="pb-2 font-medium">引擎層</th>
                <th className="pb-2 font-medium">Premium 期限</th>
                <th className="pb-2 font-medium">反濫用上限</th>
                <th className="pb-2 font-medium">對話額度</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {[
                ["lite199", "HK$199/月", "前 2 個月 Premium，期滿自動降 Basic", "開通後 60 日", "以 🛡️ 反濫用頁為準", "2,000／月"],
                ["pro499", "HK$499/月", "永久 Premium", "永久", "以 🛡️ 反濫用頁為準", "6,000／月"],
                ["legacy", "—", "永久 Premium（舊租戶）", "永久", "跟 platform_config 預設", "無限"],
                ["trial", "—", "無（試用）", "—", "不適用", "trial_quota"],
              ].map(([tier, price, engine, premium, abuse, quota]) => (
                <tr key={tier as string}>
                  <td className="py-3 pr-3 font-mono text-emerald-300 whitespace-nowrap">{tier}</td>
                  <td className="py-3 pr-3 text-slate-200 whitespace-nowrap">{price}</td>
                  <td className="py-3 pr-3 text-slate-300">{engine}</td>
                  <td className="py-3 pr-3 text-slate-400">{premium}</td>
                  <td className="py-3 pr-3 text-slate-400">{abuse}</td>
                  <td className="py-3 text-slate-400">{quota}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          方案層級會覆蓋租戶原本設定的 llm_provider / llm_model——後台「編輯租戶」的 LLM 下拉只供罕見除錯，日常由 plan_tier 決定。
        </p>
      </div>

      {/* ── 引擎矩陣 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-sky-400 mb-4">🤖 實際引擎矩陣</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-950/50 border border-sky-700/30 rounded-lg p-4">
            <div className="text-sm font-semibold text-sky-300 mb-2">Premium 層</div>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
              <li>
                文字：<span className="text-slate-200 font-mono">deepseek-v4-flash</span>
              </li>
              <li>
                識圖：<span className="text-slate-200 font-mono">deepseek-v4-flash-vision-exp</span>
              </li>
              <li>
                後備：<span className="text-slate-200">GLM-4-Flash（glm_routing 開啟時）→ CF Workers AI</span>
              </li>
              <li>
                功能閘：deepseek_routing 必開（Premium 主力引擎）
              </li>
            </ul>
          </div>
          <div className="bg-slate-950/50 border border-slate-700/30 rounded-lg p-4">
            <div className="text-sm font-semibold text-slate-300 mb-2">Basic 層（經濟引擎）</div>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
              <li>
                文字：<span className="text-slate-200 font-mono">glm-4-flash</span>
              </li>
              <li>
                識圖：<span className="text-slate-200 font-mono">glm-4v-flash</span>
              </li>
              <li>
                後備：<span className="text-slate-200">DeepSeek（deepseek_routing 開啟時）→ CF Workers AI（免費額度）</span>
              </li>
              <li>
                功能閘：deepseek_routing 預設關閉，只保留 glm_routing + vision_inbound
              </li>
            </ul>
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          2026-09-07 Captain 拍板：Premium 主力＝DeepSeek（唯一全鏈 E2E 驗證嘅引擎；識圖必用 -vision-exp 專門模型）。初版曾將兩層統一做 glm-4-flash（原規格型號 5.3／4.7-Flash 喺 Zhipu API 唔存在），已修正——GLM 保留做 Basic 免費層與各層後備。
        </div>
      </div>

      {/* ── Fallback 鏈 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-amber-400 mb-4">🔄 Fallback 鏈</h2>
        <div className="flex flex-col md:flex-row items-center gap-2 text-xs text-center mb-2">
          <div className="px-3 py-1.5 bg-slate-800 rounded text-slate-400 w-full md:w-auto">Premium：</div>
          <div className="px-4 py-2 bg-violet-950/30 border border-violet-700/40 rounded-lg">
            <div className="font-semibold text-violet-300">1. DeepSeek（v4-flash）</div>
            <div className="text-slate-500">主力引擎</div>
          </div>
          <span className="text-slate-600">→</span>
          <div className="px-4 py-2 bg-sky-950/30 border border-sky-700/40 rounded-lg">
            <div className="font-semibold text-sky-300">2. GLM（glm-4-flash）</div>
            <div className="text-slate-500">glm_routing 開啟時後備</div>
          </div>
          <span className="text-slate-600">→</span>
          <div className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg">
            <div className="font-semibold text-slate-300">3. CF Workers AI</div>
            <div className="text-slate-500">免費額度最後防線</div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-2 text-xs text-center mb-4">
          <div className="px-3 py-1.5 bg-slate-800 rounded text-slate-400 w-full md:w-auto">Basic：</div>
          <div className="px-4 py-2 bg-sky-950/30 border border-sky-700/40 rounded-lg">
            <div className="font-semibold text-sky-300">1. GLM（glm-4-flash）</div>
            <div className="text-slate-500">免費主力引擎</div>
          </div>
          <span className="text-slate-600">→</span>
          <div className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg">
            <div className="font-semibold text-slate-300">2. CF Workers AI</div>
            <div className="text-slate-500">deepseek_routing 關閉，免費防線</div>
          </div>
        </div>
        <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
          <li>Premium：主力 DeepSeek；GLM 只做後備（glm_routing 開啟時），再跌 CF Workers AI。</li>
          <li>Basic：主力 GLM；deepseek_routing 預設關閉，故障直接跌 CF Workers AI。</li>
          <li>DeepSeek 後備時，非 deepseek-* 型號會自動改寫為 deepseek-v4-flash，避免 400。</li>
          <li>識圖輪：Premium 必走 DeepSeek vision-exp；Basic 走 GLM vision（無 Zhipu key 則試 DeepSeek vision-exp；再無則降級為純文字 placeholder）。</li>
        </ul>
      </div>

      {/* ── Vision 規則 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-violet-400 mb-4">👁️ Vision 規則</h2>
        <table className="w-full text-xs">
          <tbody className="divide-y divide-slate-800">
            {[
              ["功能閘", "vision_inbound（tenant_features 或 platform_config 預設）"],
              ["支援格式", "webp / png / jpeg / gif"],
              ["掃描型 PDF", "WhatsApp 端經 connector unpdf 抽字； wizard / portal 用 pdf.js"],
              ["額度計算", "一圖一輪對話；圖片併入同一輪只計一次"],
              ["模型", "Premium＝deepseek-v4-flash-vision-exp；Basic＝glm-4v-flash（無 Zhipu key 後備 DeepSeek vision-exp）"],
              ["安全", "無可用 vision key 時，AI 只見 placeholder 文字，不會硬答"],
            ].map(([k, v]) => (
              <tr key={k as string}>
                <td className="py-2 pr-3 font-semibold text-slate-300 whitespace-nowrap w-32">{k}</td>
                <td className="py-2 text-slate-400">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Peak / Eco 節流 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-rose-400 mb-4">⚡ Peak / Eco 節流</h2>
        <div className="text-xs text-slate-400 space-y-2">
          <p>
            <span className="text-slate-200 font-semibold">Eco Peak（deepseek_eco_peak_cf）：</span>
            當 platform_config 設為 <span className="font-mono text-amber-300">1</span> 時，DeepSeek 會被判定為 peak 時段，非 vision 輪自動轉用 CF Workers AI，減少付費消耗。
          </p>
          <p>
            <span className="text-slate-200 font-semibold">判定函式：</span>
            <span className="font-mono text-slate-500">isDeepseekPeakNow()</span> —— 目前覆蓋平日 09:00–12:00、14:00–18:00、20:00–23:00 及週末多個高峰段（香港時間）。
          </p>
          <p>
            <span className="text-slate-200 font-semibold">預設狀態：</span>
            關閉（deepseek_eco_peak_cf = 0）。開啟會讓 Premium 租戶在高峰段也走免費引擎，需審慎評估。
          </p>
        </div>
      </div>

      {/* ── 反濫用規則 ── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="text-base font-bold text-rose-400 mb-4">🛡️ 反濫用規則</h2>
        <table className="w-full text-xs">
          <tbody className="divide-y divide-slate-800">
            {[
              ["計數維度", "每 tenant + 每 customer_phone + 每 30 分鐘靜默週期"],
              ["現值", "以 🛡️ 反濫用上限頁為準（後台可調；2026-10 核實＝Lite 10／Pro 15）"],
              ["觸達上限", "AI 自動暫停並提示「已達上限，請稍後再試」；不通知老闆"],
              ["後台可配", "/anti-abuse 頁可改 platform_config 與逐租戶 tenant_features 覆蓋"],
            ].map(([k, v]) => (
              <tr key={k as string}>
                <td className="py-2 pr-3 font-semibold text-slate-300 whitespace-nowrap w-32">{k}</td>
                <td className="py-2 text-slate-400">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4">
          <Link
            to="/anti-abuse"
            className="inline-block px-4 py-2 text-xs bg-rose-700/30 hover:bg-rose-700/50 text-rose-200 rounded-lg transition-colors"
          >
            前往反濫用設定 →
          </Link>
        </div>
      </div>

      {/* ── 備註 ── */}
      <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-4 text-[11px] text-slate-500">
        <p>
          以上規則來源：packages/ai-core/src/llm-engines.ts、packages/ai-core/src/index.ts、packages/ai-core/src/anti-abuse.ts。改動引擎矩陣必須同步更新本頁與 CHANGELOG。
        </p>
      </div>
    </div>
  );
}
